import React, {
  Dispatch,
  FunctionComponent,
  ReactNode,
  SetStateAction,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Accordion, Section } from '../Accordion';
import { TextLink } from '../TextLink';
import {
  useMerchantDetails,
  useMerchantPaymentProducts,
  useVirtualCard,
} from '../XPropsContext';
import SlideOut from '../SlideOut';
import { useAppConfig } from '../AppConfigContext';
import { LearnMore } from './FAQs';

type Props = {
  onBack: () => void;
  className?: string;
};

type FAQProps = Props & {
  isShowing: boolean;
  setIsShowing: Dispatch<SetStateAction<boolean>>;
};

const faqFormattingValues = {
  p: (chunks: ReactNode) => <p className="mt-4 first:mt-0 mb-1">{chunks}</p>,
  strong: (chunks: ReactNode) => <span className="font-bold">{chunks}</span>,
};

export const InStoreFAQs: FunctionComponent<FAQProps> = ({
  isShowing,
  setIsShowing,
  onBack,
}) => {
  const { hasInstallments, hasSplitPay, hasHybridLineOfCredit } =
    useMerchantPaymentProducts();
  const { isVirtualCard } = useVirtualCard();
  return (
    <SlideOut
      id="in-store-faqs-slideout"
      isOpen={isShowing}
      onClose={() => setIsShowing(false)}
      ariaLabel={useIntl().formatMessage({
        defaultMessage: 'In-Store Frequently Asked Questions',
        description: 'In-Store FAQs aria label',
      })}
      afterLeave={() => onBack()}
    >
      {isVirtualCard ? (
        <VirtualCardGeneralFAQs hasHybridLineOfCredit={hasHybridLineOfCredit} />
      ) : (
        <InStoreGeneralFAQs />
      )}
      {isVirtualCard && (hasInstallments || hasSplitPay) && (
        <InStoreOneTimeUseCardFAQs />
      )}

      {hasInstallments && <InStoreInstallmentsFAQs />}
      {hasSplitPay && <InStoreSplitPayFAQs />}
      {hasHybridLineOfCredit && <InStoreHybridLineOfCreditFAQs />}

      {isVirtualCard && hasHybridLineOfCredit && (
        <HybridLineOfCreditVirtualCardGeneralFAQs />
      )}

      <LearnMore />
    </SlideOut>
  );
};

const InStoreGeneralFAQs: FunctionComponent = () => {
  const intl = useIntl();
  const { supportUrl, helpPhone } = useAppConfig();
  return (
    <div>
      <h2 id="general-questions" className="text-lg font-extrabold mb-4">
        <FormattedMessage
          defaultMessage="General Questions"
          description="FAQ Screen General heading"
        />
      </h2>
      <Accordion>
        <Section
          title={intl.formatMessage({
            defaultMessage: 'What is Bread Pay®?',
            description: 'FAQ Screen General question 1',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>Bread Pay® is a pay-over-time technology solution that helps you make purchases with easy-to-understand equal payments. Bread Pay® products include the Bread Pay® Installment plan, with terms from 3 to 48 months, and the Bread Pay® SplitPay plan, with four payments made every two weeks, each payment being 25% of the original purchase amount.</p>"
            description="FAQ Screen General answer 1"
            values={faqFormattingValues}
          />
        </Section>

        <Section
          title={intl.formatMessage({
            defaultMessage:
              'I want to find out if I’m eligible for Bread Pay®. Will this impact my credit score?',
            description: 'FAQ Screen General question 2',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>Checking your eligibility does not affect your credit score since only a soft inquiry is completed. If you choose a Bread Pay® payment plan after your eligibility is confirmed, we will not perform a hard inquiry with any of the credit reporting agencies.</p>

          <p>Bread Pay® may report repayment information about your account to the consumer reporting agencies, which may impact your credit score.</p>"
            description="FAQ Screen General answer 2"
            values={faqFormattingValues}
          />
        </Section>

        <Section
          title={intl.formatMessage({
            defaultMessage:
              'Does prequalifying for Bread Pay® impact my credit score?',
            description: 'FAQ Screen General question 3',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>Checking your eligibility does not affect your credit score since only a soft inquiry is completed. If you choose a Bread Pay® payment plan after your eligibility is confirmed, we will not perform a hard inquiry with any of the credit reporting agencies.</p>

          <p>Bread Pay® may report repayment information about your account to the consumer reporting agencies, which may impact your credit score.</p>"
            description="FAQ Screen General answer 3"
            values={faqFormattingValues}
          />
        </Section>

        <Section
          title={intl.formatMessage({
            defaultMessage: 'How is my credit eligibility determined?',
            description: 'FAQ Screen General question 4',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>We work with our bank affiliate, Comenity Capital Bank, which determines eligibility by reviewing factors such as your credit score and repayment history.</p>"
            description="FAQ Screen General answer 4"
            values={faqFormattingValues}
          />
        </Section>

        <Section
          title={intl.formatMessage({
            defaultMessage:
              'How do I make an in-store purchase with Pay Later?',
            description: 'FAQ Screen General question 6',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>With Pay Later, you’ll receive a confirmation code to use at checkout. This confirmation code is valid only at the retail partner's physical store and will expire within 12 hours. You'll need to present your confirmation code to the retail store associate with a valid photo ID to check out with your selected payment plan.</p>
                "
            description="FAQ Screen General answer 6"
            values={faqFormattingValues}
          />
        </Section>

        <Section
          title={intl.formatMessage({
            defaultMessage: 'How much can I spend in-store with Pay Later?',
            description: 'FAQ Screen General question 5',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>The amount you can spend in-store with Pay Later is based on your eligibility and the payment plan you select. If you qualify for a payment plan, you'll be presented with the amount you can spend up to with each payment product. Your payment amount will be based on your final purchase amount and your loan will not be completed until you check out with your confirmation code at the register in the retailer's physical store location.</p>"
            description="FAQ Screen General answer 5"
            values={faqFormattingValues}
          />
        </Section>

        <Section
          title={intl.formatMessage({
            defaultMessage:
              'What should I do if I am not receiving my mobile verification code?',
            description: 'FAQ Screen General question 7',
          })}
        >
          <FormattedMessage
            defaultMessage='<p>Please wait a few minutes before requesting a new mobile verification code to avoid receiving multiple texts with different codes all at once.
          The phone number you provide must be a mobile number that is able to receive text messages.</p>

          <p>You’ll have five attempts to validate your mobile verification code before being blocked for 15 minutes.
             If you are still not receiving a mobile verification code, we recommend you request a new verification code by selecting "Resend code".</p>

          <p>We’re unable to send codes to prepaid phones. If you have this type of phone or need additional assistance receiving a code, please contact our Member Support team <link>here</link>.</p>'
            description="FAQ Screen General answer 7"
            values={{
              ...faqFormattingValues,
              link: (chunks: ReactNode) => (
                <TextLink
                  href={supportUrl}
                  target="_blank"
                  testID="customer-support"
                >
                  {chunks}
                </TextLink>
              ),
            }}
          />
        </Section>

        <Section
          title={intl.formatMessage({
            defaultMessage: 'How do I contact customer support?',
            description: 'FAQ Screen General question 8',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>You can reach our Customer Support team at <tel>{phoneNumber}</tel> or <mail>support@breadpayments.com</mail>. For more information about our hours of operation, visit our <link>Contact Us</link> page.</p>"
            description="FAQ Screen General answer 8"
            values={{
              ...faqFormattingValues,
              phoneNumber: helpPhone,
              tel: (chunks: ReactNode) => (
                <TextLink
                  href={`tel:${helpPhone}`}
                  testID="customer-support-phone"
                >
                  {chunks}
                </TextLink>
              ),
              mail: (chunks: ReactNode) => (
                <TextLink
                  href="mailto:support@breadpayments.com"
                  testID="customer-support-email"
                >
                  {chunks}
                </TextLink>
              ),
              link: (chunks: ReactNode) => (
                <TextLink
                  href={supportUrl}
                  target="_blank"
                  testID="customer-support-link"
                >
                  {chunks}
                </TextLink>
              ),
            }}
          />
        </Section>
      </Accordion>
    </div>
  );
};

interface VirtualCardGeneralFAQsProps {
  hasHybridLineOfCredit: boolean;
}
const VirtualCardGeneralFAQs: FunctionComponent<VirtualCardGeneralFAQsProps> =
  ({ hasHybridLineOfCredit }) => {
    const intl = useIntl();
    const { supportUrl } = useAppConfig();
    return (
      <div>
        <h2 id="general-questions" className="text-lg font-extrabold mb-4">
          <FormattedMessage
            defaultMessage="General Questions"
            description="FAQ Screen General heading"
          />
        </h2>
        <Accordion>
          <Section
            title={intl.formatMessage({
              defaultMessage: 'What is Bread Pay®?',
              description: 'FAQ Screen General question 1',
            })}
          >
            <FormattedMessage
              defaultMessage="<p>Bread Pay® provides flexible payment plans that allow you to pay over time with monthly installments or split your purchase into four interest-free payments. If eligible, you can use Bread Pay to shop at participating retailers for both online and in-store purchases.</p><p>Want to learn more? <link>Visit our Help Center</link>.</p>"
              description="FAQ Screen General answer 1"
              values={{
                ...faqFormattingValues,
                link: (chunks: ReactNode) => (
                  <TextLink
                    href={supportUrl}
                    target="_blank"
                    testID="support-link"
                  >
                    {chunks}
                  </TextLink>
                ),
              }}
            />
          </Section>

          <Section
            title={intl.formatMessage({
              defaultMessage:
                'Will my credit score be affected if I check my eligibility for Bread Pay®?',
              description: 'FAQ Screen General question 2',
            })}
          >
            <FormattedMessage
              defaultMessage="<p>No, your credit score won’t be affected if you check your eligibility. We use a soft credit inquiry, which has no impact on your score, unlike a hard credit pull.</p>
              <p>However, we may report your loan repayment history to the consumer reporting agencies, which could impact your credit score.</p>"
              description="FAQ Screen General answer 2"
              values={faqFormattingValues}
            />
          </Section>

          <Section
            title={intl.formatMessage({
              defaultMessage: 'Are there any fees to use Bread Pay®?',
              description: 'FAQ Screen General question 5',
            })}
          >
            <FormattedMessage
              defaultMessage="<p>There are no origination fees or prepayment penalties, but late payment fees may apply if payments are missed or not made on time. Be sure to review your loan agreement for more information.</p>"
              description="FAQ Screen General answer 5"
              values={faqFormattingValues}
            />
          </Section>

          <Section
            title={intl.formatMessage({
              defaultMessage:
                'What should I do if I am not receiving my mobile verification code?',
              description: 'FAQ Screen General question 7',
            })}
          >
            <FormattedMessage
              defaultMessage="<p>First, ensure the phone number you entered is a U.S.-based mobile number capable of receiving text messages. Please note, we cannot send codes to prepaid phone services or landlines at this time.</p>

          <p>It may take a few minutes for your code to arrive, so please wait before requesting another. If you still haven’t received your code, select <strong>Resend code</strong> on the mobile verification page. You’ll have up to five attempts to enter your code before being temporarily locked out for 15 minutes.</p>

          <p>Need more help? <link>Contact our Customer Support team.</link></p>"
              description="FAQ Screen General answer 7"
              values={{
                ...faqFormattingValues,
                link: (chunks: ReactNode) => (
                  <TextLink
                    href={supportUrl}
                    target="_blank"
                    testID="customer-support"
                  >
                    {chunks}
                  </TextLink>
                ),
              }}
            />
          </Section>

          <Section
            title={intl.formatMessage({
              defaultMessage: 'Why do I need to upload my ID?',
              description: 'FAQ Screen General question 9',
            })}
          >
            <FormattedMessage
              defaultMessage="We may ask you to upload a valid government-issued ID to confirm your identity. This helps us make sure it’s really you applying for the loan and helps protect against fraud. After your identity has been verified, you can finalize your application and complete your purchase."
              description="FAQ Screen General answer 9"
            />
          </Section>

          {hasHybridLineOfCredit && (
            <Section
              title={intl.formatMessage({
                defaultMessage: 'What forms of payment are accepted?',
                description: 'FAQ Screen General question 10',
              })}
            >
              <FormattedMessage
                defaultMessage="<p>You can make payments through ACH electronic transfers from your bank account or with a personal debit card. We do not accept credit cards, physical checks, business debit cards, bill pay services, or cards tied to Flexible Spending Accounts (FSA) or Health Savings Accounts (HSA).</p>
                <p>Late payment fees may apply if payments are not received by 11:59 p.m. Eastern Time (ET) on your due date. For full payment details, please review your loan agreement.</p>"
                description="FAQ Screen General answer 10"
                values={faqFormattingValues}
              />
            </Section>
          )}
        </Accordion>
      </div>
    );
  };

const InStoreInstallmentsFAQs: FunctionComponent = () => {
  const intl = useIntl();
  const { isVirtualCard } = useVirtualCard();

  return (
    <div>
      <h2 id="installments-faqs" className="text-lg font-extrabold mb-4">
        <FormattedMessage
          defaultMessage="Installments Questions"
          description="FAQ Screen Installments heading"
        />
      </h2>
      <Accordion>
        <Section
          title={intl.formatMessage({
            defaultMessage: 'What is a Bread Pay® Installment plan?',
            description: 'FAQ Screen Installments question 1',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>A Bread Pay® Installment plan is a pay-over-time technology solution that helps you make purchases with easy-to-understand, equal payments. With our installment plans, you pay us back monthly over 3 to 48 months with APRs ranging from 0% to 29.99%. Keep in mind, the available loan terms may vary based on the retailer.</p>"
            description="FAQ Screen Installments answer 1"
            values={faqFormattingValues}
          />
        </Section>

        <Section
          title={intl.formatMessage({
            defaultMessage:
              'Am I eligible to apply for a Bread Pay® Installment plan?',
            description: 'FAQ Screen Installments question 2',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>To apply for a Bread Pay® Installment plan, you must meet your state’s age of majority – the age your state of residence defines as required to legally enter into a contract. You’ll also need a valid phone number, email address and mobile device that allows for a digital wallet.</p>

            <p>Bread Financial® must be able to verify your identity based on the information you provide in the application. Checking your eligibility does not affect your credit score since only a soft inquiry is completed.</p>"
            description="FAQ Screen Installments answer 2"
            values={faqFormattingValues}
          />
        </Section>

        {isVirtualCard && (
          <Section
            title={intl.formatMessage({
              defaultMessage: 'How is my credit eligibility determined?',
              description: 'FAQ Screen General question 4',
            })}
          >
            <FormattedMessage
              defaultMessage="<p>We work with our bank affiliate, Comenity Capital Bank, which determines eligibility by reviewing factors such as your credit score and repayment history.</p>"
              description="FAQ Screen General answer 4"
              values={faqFormattingValues}
            />
          </Section>
        )}

        <Section
          title={intl.formatMessage({
            defaultMessage:
              'How is the APR on my Bread Pay® Installment plan determined?',
            description: 'FAQ Screen Installments question 3',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>Our bank affiliate, Comenity Capital Bank, determines the APR based on a variety of factors. These factors typically include your credit history and credit score, amount financed and length of the term, though other factors may be considered.</p>"
            description="FAQ Screen Installments answer 3"
            values={faqFormattingValues}
          />
        </Section>

        <Section
          title={intl.formatMessage({
            defaultMessage:
              'What installment plan payment methods do you accept?',
            description: 'FAQ Screen Installments question 4',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>You can make payments on Bread Pay® Installment plans using ACH funds transfer or a personal debit card. You cannot make payments on Bread Pay® Installment plans with a credit card, business debit card, bill pay or check. Additionally, Bread Pay® does not accept Flexible Spending Account (FSA) or Health Savings Account (HSA) payment cards because we are not designated as a health services retailer. Payments must be received by 5:00 p.m. Eastern Time (ET) on your due date.</p>"
            description="FAQ Screen Installments answer 4"
            values={faqFormattingValues}
          />
        </Section>
      </Accordion>
    </div>
  );
};

const InStoreSplitPayFAQs: FunctionComponent = () => {
  const intl = useIntl();
  const { isVirtualCard } = useVirtualCard();

  return (
    <div>
      <h2 id="splitpay-faqs" className="text-lg font-extrabold mb-4">
        <FormattedMessage
          defaultMessage="SplitPay Questions"
          description="FAQ Screen SplitPay heading"
        />
      </h2>
      <Accordion>
        <Section
          title={intl.formatMessage({
            defaultMessage: 'What is Bread Pay® SplitPay?',
            description: 'FAQ Screen SplitPay question 1',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>Bread Pay® SplitPay is a payment option that enables you to pay for your purchase with four equal and interest-free payments using a personal credit card or debit card.</p>"
            description="FAQ Screen SplitPay answer 1"
            values={faqFormattingValues}
          />
        </Section>

        <Section
          title={intl.formatMessage({
            defaultMessage:
              'Am I eligible to apply for a Bread Pay® SplitPay plan?',
            description: 'FAQ Screen SplitPay question 2',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>To apply for a Bread Pay® SplitPay plan, you must meet your state’s age of majority – the age your state of residence defines as required to legally enter into a contract. You’ll also need a valid phone number, email address and mobile device that allows for a digital wallet.</p>

            <p>Bread Financial® must be able to verify your identity based on the information you provide in the application. Checking your eligibility does not affect your credit score since only a soft inquiry is completed.</p>"
            description="FAQ Screen SplitPay answer 2"
            values={faqFormattingValues}
          />
        </Section>

        {isVirtualCard && (
          <Section
            title={intl.formatMessage({
              defaultMessage:
                'I want to find out if I’m eligible for Bread Pay® SplitPay. Will this impact my credit score?',
              description: 'FAQ Screen General question 2',
            })}
          >
            <FormattedMessage
              defaultMessage="<p>Checking your eligibility does not affect your credit score since only a soft inquiry is completed. If you choose a Bread Pay® payment plan after your eligibility is confirmed, we will not perform a hard inquiry with any of the credit reporting agencies.</p>

          <p>Bread Pay® may report repayment information about your account to the consumer reporting agencies, which may impact your credit score.</p>"
              description="FAQ Screen General answer 2"
              values={faqFormattingValues}
            />
          </Section>
        )}

        <Section
          title={intl.formatMessage({
            defaultMessage:
              'What Bread Pay® SplitPay payment methods do you accept?',
            description: 'FAQ Screen SplitPay question 3',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>You can make payments on Bread Pay® SplitPay using a personal debit card or credit card. You cannot make Bread Pay® SplitPay payments with a business debit card, ACH funds transfer or check. Additionally, Bread Pay® does not accept Flexible Spending Account (FSA) or Health Savings Account (HSA) payment cards because we are not designated as a health services retailer. Please note, payments must be received by 5:00 p.m. Eastern Time (ET) on your due date.</p>"
            description="FAQ Screen SplitPay answer 3"
            values={faqFormattingValues}
          />
        </Section>
      </Accordion>
    </div>
  );
};

const InStoreHybridLineOfCreditFAQs: FunctionComponent = () => {
  const intl = useIntl();
  const { name: merchantName } = useMerchantDetails();
  const { memberPortalUrl } = useAppConfig();

  return (
    <div>
      <h2
        id="hybrid-line-of-credit-faqs"
        className="text-lg font-extrabold mb-4"
      >
        <FormattedMessage
          defaultMessage="About Bread Pay® FlexLoans"
          description="FAQ Screen Hybrid Line of Credit heading"
        />
      </h2>
      <Accordion>
        <Section
          title={intl.formatMessage({
            defaultMessage: 'What is a Bread Pay® FlexLoan?',
            description: 'FAQ Screen Hybrid Line of Credit question 1',
          })}
        >
          <FormattedMessage
            defaultMessage="<p> A FlexLoan is a simple and flexible payment plan that helps you pay for multiple purchases over time. Once approved, you can use your FlexLoan to shop at {merchantName} for up to six months or until you reach your credit limit.</p><p>FlexLoan terms range from 60-120 months, with an APR of 9.99%. In select locations a 0% APR offer may be available depending on your credit profile.</p>"
            description="FAQ Screen Hybrid Line of Credit answer 1"
            values={{
              ...faqFormattingValues,
              merchantName,
            }}
          />
        </Section>

        <Section
          title={intl.formatMessage({
            defaultMessage: 'Am I eligible to apply?',
            description: 'FAQ Screen Hybrid Line of Credit question 2',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>To apply for a FlexLoan, you must be a legal U.S. resident, meet your state's legal age requirement for entering into contracts, and have the following: a valid U.S. government-issued photo ID, a U.S.-based phone number, an email address, and a mobile device compatible with a digital wallet.</p><p>To check your eligibility, we’ll need to verify your identity using the information provided in your loan application. This won’t impact your credit score.</p>"
            description="FAQ Screen Hybrid Line of Credit answer 2"
            values={faqFormattingValues}
          />
        </Section>

        <Section
          title={intl.formatMessage({
            defaultMessage: 'Where can I shop with a FlexLoan?',
            description: 'FAQ Screen Hybrid Line of Credit question 3',
          })}
        >
          <FormattedMessage
            defaultMessage="Your FlexLoan funds, accessible through a virtual card, can be used exclusively at {merchantName} for multiple purchases over a six-month period or until you reach your credit limit."
            description="FAQ Screen Hybrid Line of Credit answer 3"
            values={{
              ...faqFormattingValues,
              merchantName,
            }}
          />
        </Section>

        <Section
          title={intl.formatMessage({
            defaultMessage:
              'Does my available credit increase as I pay down my loan balance? ',
            description: 'FAQ Screen Hybrid Line of Credit question 4',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>No, your credit limit, or prequalified amount, is set once your application has been approved and is available only for a six-month period.</p><p>While paying down your balance reduces what you owe, those funds will not be available for purchases. If you make a return or a transaction is canceled those funds will become available for use again as long as the refund is processed within your Purchase Window. You can check your available credit anytime in the <link>Member Portal</link>.</p>"
            description="FAQ Screen Hybrid Line of Credit answer 4"
            values={{
              ...faqFormattingValues,
              link: (chunks: ReactNode) => (
                <TextLink
                  href={memberPortalUrl}
                  target="_blank"
                  testID="hyloc-member-portal-link"
                >
                  {chunks}
                </TextLink>
              ),
              merchantName,
            }}
          />
        </Section>

        <Section
          title={intl.formatMessage({
            defaultMessage:
              'Will my credit limit change after a refund is processed?',
            description: 'FAQ Screen Hybrid Line of Credit question 5',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>Returns made within the six-month purchase window of your plan will be refunded to your virtual card, making the funds available to spend again.
            However, if the purchase window has closed, any refunds will be applied directly to your loan balance. While this reduces your outstanding balance, it does not make the funds available for spending.</p>
            <p>Please note that all returns are subject to the retailer’s return policy. To ensure seamless returns, add your virtual card to your digital wallet or securely store the card details for easy access.</p>"
            description="FAQ Screen Hybrid Line of Credit answer 5"
            values={faqFormattingValues}
          />
        </Section>
      </Accordion>
    </div>
  );
};

const InStoreOneTimeUseCardFAQs: FunctionComponent = () => {
  const intl = useIntl();
  const { helpPhone, supportUrl } = useAppConfig();

  return (
    <div>
      <h2
        id="in-store-one-time-use-card-faqs"
        className="text-lg font-extrabold mb-4"
      >
        <FormattedMessage
          defaultMessage="In-Store One-Time Use Card FAQs"
          description="FAQ Screen In-Store One-Time UseCard heading"
        />
      </h2>
      <Accordion>
        <Section
          title={intl.formatMessage({
            defaultMessage: 'What is a BreadPay® One-Time Use Card?',
            description: 'FAQ Screen In-Store One-Time UseCard question 1',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>The Bread Pay® One-Time Use Card is a virtual card that can be used in-store on a purchase. It’s valid for seven days. Once you’re approved for the Bread Pay® Installment plan or Bread Pay® SplitPay plan, simply add the card to your digital wallet and tap to pay with your phone.</p>"
            description="FAQ Screen In-Store One-Time UseCard answer 1"
            values={faqFormattingValues}
          />
        </Section>

        <Section
          title={intl.formatMessage({
            defaultMessage:
              'How do I apply for the Bread Pay® One-Time Use Card?',
            description: 'FAQ Screen In-Store One-Time UseCard question 2',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>Scan the QR code in-store to learn more and start the application. Once you’re approved for the Bread Pay® Installment plan or Bread Pay® SplitPay plan, simply add the card to your digital wallet and tap to pay with your phone.</p>"
            description="FAQ Screen In-Store One-Time UseCard answer 2"
            values={faqFormattingValues}
          />
        </Section>

        <Section
          title={intl.formatMessage({
            defaultMessage:
              'Am I eligible to apply for a Bread Pay® One-Time Use Card?',
            description: 'FAQ Screen In-Store One-Time UseCard question 6',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>To apply for a Bread Pay® One-Time Use Card, you must you meet your state’s age of majority – the age your state of residence defines as required to legally enter into a contract. You’ll also need a valid phone number, email address and mobile device that allows for a digital wallet.</p>

            <p>Bread Financial® must be able to verify your identity based on the information you provide in the application. Checking your eligibility does not affect your credit score since only a soft inquiry is completed.</p>"
            description="FAQ Screen In-Store One-Time UseCard answer 6"
            values={faqFormattingValues}
          />
        </Section>

        <Section
          title={intl.formatMessage({
            defaultMessage: 'Where can I use my Bread Pay® One-Time Use Card?',
            description: 'FAQ Screen In-Store One-Time UseCard question 3',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>You can use the card in-store at a participating merchant location. You must have previously applied and been approved with this merchant for a Bread Pay® Installment plan or Bread Pay® SplitPay plan.</p>"
            description="FAQ Screen In-Store One-Time UseCard answer 3"
            values={faqFormattingValues}
          />
        </Section>

        <Section
          title={intl.formatMessage({
            defaultMessage: 'How do I make an in-store purchase with the card?',
            description: 'FAQ Screen In-Store One-Time UseCard question 4',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>Once you’ve selected your eligible Bread Pay® Installment plan or Bread Pay® SplitPay plan and you’re approved, add your card to your digital wallet then just tap to pay at the checkout.</p>"
            description="FAQ Screen In-Store One-Time UseCard answer 4"
            values={faqFormattingValues}
          />
        </Section>

        <Section
          title={intl.formatMessage({
            defaultMessage:
              'How much can I spend in-store with my Bread Pay® One-Time Use Card?',
            description: 'FAQ Screen In-Store One-Time UseCard question 5',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>The amount you can spend in-store with your Bread Pay® One-Time Use Card is based on your eligibility and the payment plan you select. If you qualify for a payment plan, you'll be presented with the amount you can spend up to with each payment product. Your payment amount will be based on your final purchase amount and your loan will not be completed until you use the card in your digital wallet at the register to complete your purchase.</p>"
            description="FAQ Screen In-Store One-Time UseCard answer 5"
            values={faqFormattingValues}
          />
        </Section>

        <Section
          title={intl.formatMessage({
            defaultMessage: 'How do I add my card to my digital wallet?',
            description: 'FAQ Screen In-Store One-Time UseCard question 7',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>Once your application has been approved, you’ll be prompted to add the Bread Pay® One-Time Use Card to your digital wallet. On the “Add Card to Wallet” screen, select the wallet for your device, either Apple Wallet or Google Pay. After successfully adding to your wallet, your card is all set!</p>"
            description="FAQ Screen In-Store One-Time UseCard answer 7"
            values={faqFormattingValues}
          />
        </Section>
        <Section
          title={intl.formatMessage({
            defaultMessage: 'Is my card safe and secure in my digital wallet?',
            description: 'FAQ Screen In-Store One-Time UseCard question 8',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>Yes! Digital wallets use multi-layered security to keep your card information safe. Your personal information is never shared with the merchant or stored on the mobile device. Checkout is fast and simple – with added peace of mind.</p>"
            description="FAQ Screen In-Store One-Time UseCard answer 8"
            values={faqFormattingValues}
          />
        </Section>
        <Section
          title={intl.formatMessage({
            defaultMessage:
              ' Help! I can’t check out with my Bread Pay® One-Time Use Card.',
            description: 'FAQ Screen In-Store One-Time UseCard question 9',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>Having trouble locating your card? Please check your digital wallet and look for the Bread Pay® One-Time Use Card. If you do not see the card there, you can access it on the confirmation page of your Bread Pay® application.</p>

            <p>Having trouble adding your card? For help setting up your digital wallet, please visit <apple>Apple</apple> or <google>Google</google>. When you’re adding the one-time use card to your mobile device, a security verification will be required. If your mobile device cannot be verified, you may not be able to complete your transaction. For issues with using or accessing your card, please visit our Member Portal. You may also <link>contact us</link>.</p>"
            description="FAQ Screen In-Store One-Time UseCard answer 9"
            values={{
              ...faqFormattingValues,
              apple: (chunks: ReactNode) => (
                <TextLink
                  href="https://support.apple.com/apple-pay"
                  target="_blank"
                  testID="apple-support-link"
                >
                  {chunks}
                </TextLink>
              ),
              google: (chunks: ReactNode) => (
                <TextLink
                  href="https://support.google.com/googlepay/?hl=en#topic=11450859"
                  target="_blank"
                  testID="google-support-link"
                >
                  {chunks}
                </TextLink>
              ),
              link: (chunks: ReactNode) => (
                <TextLink
                  href={supportUrl}
                  target="_blank"
                  testID="customer-support-link"
                >
                  {chunks}
                </TextLink>
              ),
            }}
          />
        </Section>
        <Section
          title={intl.formatMessage({
            defaultMessage:
              'Can I make returns with my Bread Pay® One-Time Use Card? ',
            description: 'FAQ Screen In-Store One-Time UseCard question 10',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>Yes, you can make a return, subject to the merchant return policy. We suggest you keep a receipt for any returns.</p>

            <p>If you don’t have a receipt and the merchant is unable to find your original payment method, you can log in to the Bread Financial® Member Portal to find your Bread Pay® One-Time Use Card number or contact Customer Support at <tel>{phoneNumber}</tel>.</p>
            "
            description="FAQ Screen In-Store One-Time UseCard answer 10"
            values={{
              ...faqFormattingValues,
              phoneNumber: helpPhone,
              tel: (chunks: ReactNode) => (
                <TextLink
                  href={`tel:${helpPhone}`}
                  testID="customer-support-phone"
                >
                  {chunks}
                </TextLink>
              ),
            }}
          />
        </Section>
      </Accordion>
    </div>
  );
};

const HybridLineOfCreditVirtualCardGeneralFAQs: FunctionComponent = () => {
  const intl = useIntl();
  const { memberPortalUrl } = useAppConfig();
  const { name: merchantName } = useMerchantDetails();

  return (
    <div>
      <h2
        id="hyloc-virtual-card-questions"
        className="text-lg font-extrabold mb-4"
      >
        <FormattedMessage
          defaultMessage="Using Your Virtual Card"
          description="FAQ Screen General heading"
        />
      </h2>
      <Accordion>
        <Section
          title={intl.formatMessage({
            defaultMessage:
              'What is a virtual card and how can I use it to complete my  purchase?',
            description: 'HYLOC Virtual Card FAQ Screen question 1',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>A virtual card is a digital form of payment that can be used for in-store
              and online shopping. Once your FlexLoan is approved, you’ll receive a virtual card
              exclusively for use at {merchantName}. To complete your purchase, simply add
              the card to your digital wallet to tap and pay, or manually enter the card details at
              checkout.</p><p>Please note that your first purchase must be made within 30 days of approval, or
              your loan will be canceled. After completing your first purchase, you can continue
              using your virtual card for up to six months or until you reach your credit limit.</p><p>Planning
              to buy a gift card with your FlexLoan? Remember, gift cards are available for in-store purchase
              only and cannot be bought online.</p>"
            description="HYLOC Virtual Card FAQ Screen answer 1"
            values={{
              ...faqFormattingValues,
              merchantName,
            }}
          />
        </Section>

        <Section
          title={intl.formatMessage({
            defaultMessage: 'How can I access my card?',
            description: 'HYLOC Virtual Card FAQ Screen question 2',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>After your first purchase, you can access your virtual card any time in your digital wallet or through the <link>Member Portal</link>.</p>"
            description="HYLOC Virtual Card FAQ Screen answer 2"
            values={{
              ...faqFormattingValues,
              link: (chunks: ReactNode) => (
                <TextLink
                  href={memberPortalUrl}
                  target="_blank"
                  testID="member-portal-link"
                >
                  {chunks}
                </TextLink>
              ),
            }}
          />
        </Section>

        <Section
          title={intl.formatMessage({
            defaultMessage:
              'How many purchases can I make with my virtual card?',
            description: 'HYLOC Virtual Card FAQ Screen question 3',
          })}
        >
          <FormattedMessage
            defaultMessage="<p>You can use your virtual card for multiple purchases during the six-month spending window, as long as you stay within your approved credit limit and
              meet the minimum purchase requirement. You can find purchase requirements in
              your Loan Agreement on the welcome page.</p><p>Keep in mind, your virtual card needs to be used for at least one purchase within 30 days of approval, or your loan will be canceled.</p>"
            description="HYLOC Virtual Card FAQ Screen answer 3"
            values={faqFormattingValues}
          />
        </Section>
      </Accordion>
    </div>
  );
};
